<!-- Copyright (c) 2023, Alix Mougenot (Au pied des Arbres), alix.mougenot@gmail.com -->
<!-- See license file. -->
<script setup lang="ts">
import type { Ref } from "vue";
import type { HealthCenter } from "@/types";
import HealthCenterDisplay from "@/components/actors/HealthCenterDisplay.vue";
import RelatedDossier from "@/components/dossier/RelatedDossier.vue";
import FormInput from "../FormInput.vue";

import { ref, computed, watch, inject } from "vue";
import { makeSearchEngine } from "../../logic/search";

import { MDBSwitch } from "mdb-vue-ui-kit";
import { healthCentersKey, importsKey } from "@/logic/provide";
import { useRoute } from "vue-router";
import {
  closeSecondaryViewKey,
  navigationKey,
  openSecondaryViewKey,
} from "../navigation/navigation";
import Navigation from "../navigation/Navigation.vue";

export type CenterFilter = {
  withCases: boolean;
};

const offsetShift = 24 + 15 + 1; // 24px for the header, 15px for the padding, 1px for the border
const minifiedShift = -283.64; // panel height difference between minified and full view

const route = useRoute();
const allCenters = inject(healthCentersKey);
const openSecondaryView = inject(openSecondaryViewKey);
const closeSecondaryView = inject(closeSecondaryViewKey);
const currentNavKey = inject(navigationKey);
const imports = inject(importsKey);

const latestImport = computed(() => {
  if (!imports) {
    return null;
  }
  const record = imports.value.find((i) => i.kind === "FINESS");
  if (!record) {
    return null;
  }

  return new Date(Date.parse(record.timestamp));
});

// Handle link into this page to setup the search filter
const searchInput = ref("");
const searchFilter: Ref<CenterFilter> = ref({
  withCases: false,
});

function updateSearchFromLink() {
  if (route.params.id) {
    const id = route.params.id as string;
    searchInput.value = id ? id : "";
    searchFilter.value.withCases = false;
  } else {
    searchInput.value = "";
    searchFilter.value.withCases = false;
  }
}
watch(route, updateSearchFromLink, { immediate: true, deep: true });

// Handles selected case
const _selectedCenter = ref(null) as Ref<HealthCenter | null>;
const panelX = ref(15 + offsetShift); // X placement of the event panel in the view

function centerSelected(selected: HealthCenter, offset: number) {
  if (window.getSelection()?.toString()) {
    return;
  }

  if (_selectedCenter.value && _selectedCenter.value.id === selected.id) {
    _selectedCenter.value = null;
  } else {
    // When another element is selected above it will shrink its panel and the global shift must be addapted
    var selectedIsAbove = false;
    for (let i = 0; i < currentCenters.value.length; i++) {
      if (currentCenters.value[i].id === _selectedCenter.value?.id) {
        selectedIsAbove = true;
      }
      if (currentCenters.value[i].id === selected.id) {
        break;
      }
    }
    const extraShift = selectedIsAbove ? minifiedShift : 0;
    _selectedCenter.value = selected;
    panelX.value = offset + extraShift + offsetShift; // accouting for elements on top
  }
}

function deselect() {
  if (_selectedCenter.value) {
    _selectedCenter.value = null;
  }
}

const currentCenter = computed(() => _selectedCenter.value);
watch(_selectedCenter, (newValue) => {
  if (currentNavKey) {
    if (newValue !== null) {
      openSecondaryView?.(currentNavKey);
    } else {
      closeSecondaryView?.(currentNavKey);
    }
  }
});

// Hanlde filtering
const centers = computed(() => {
  if (!allCenters) {
    return [];
  }
  return allCenters.value.list;
});

// search (part of filter)
const { searchResult } = makeSearchEngine(
  {
    includeScore: true,
    ignoreLocation: true,
    findAllMatches: true,
    minMatchCharLength: 3,
    keys: [
      { name: "id" },
      { name: "name", weight: 5 },
      { name: "other_name", weight: 4 },
      { name: "contact_imported.address", weight: 2 },
      { name: "contact_imported.zipcode", weight: 3 },
      { name: "notes" },
    ],
  },
  searchInput,
  centers
);

watch(
  searchResult,
  () => {
    // Auto open singled-out searches
    if (searchResult.value.length === 1) {
      _selectedCenter.value = searchResult.value[0] as HealthCenter;
    } else {
      _selectedCenter.value = null;
    }
  },
  { immediate: true }
);

const filteredCenters = computed(() => {
  const source = searchResult.value?.length
    ? (searchResult.value as Array<HealthCenter>)
    : centers.value;

  return source.filter(
    (act) => !searchFilter.value.withCases || act.cases.length
  );
});

const currentPage: Ref<number> = ref(0);
const maxPage = computed(
  () => Math.ceil(filteredCenters.value.length / 20) - 1
);
const currentCenters = computed(() => {
  return filteredCenters.value.slice(0, (currentPage.value + 1) * 20);
});
const loadMore = () => {
  if (currentPage.value < maxPage.value) {
    currentPage.value += 1;
  }
};

watch(searchFilter, deselect, { deep: true });
watch(currentCenters, () => {
  if (currentCenter.value) {
    if (
      currentCenters.value.find((a) => a.id === currentCenter.value?.id) ===
      undefined
    ) {
      deselect();
    }
  }
});
</script>

<template>
  <Teleport to="#toolbar">
    <span>Recherche dans :</span>
    <div>
      <MDBSwitch
        v-model="searchFilter.withCases"
        label="Impliqué dans un dossier"
      />
    </div>
  </Teleport>
  <Teleport to="#searchbar">
    <FormInput v-model="searchInput" label="Recherche" type="text" />
  </Teleport>
  <div class="centers">
    <div class="container-cntrs">
      <div class="container-stats">
        <div class="info">
          <span v-if="latestImport">
            Le dernier import FINESS date du
            {{ latestImport.toLocaleDateString() }}</span
          >
        </div>
        <div class="container-count">
          <span>{{ filteredCenters.length }}</span>
          <span v-if="filteredCenters.length <= 1">
            {{ " centre " }}
          </span>
          <span v-else>
            {{ " centres" }}
          </span>
        </div>
      </div>
      <div class="container-listing">
        <div>
          <br />
          <h3>Fichier des centres de santé</h3>
          <span>
            Ce fichier contient les centres importés depuis la base
            FINESS.</span
          ><br />
          <span
            >Vous pouvez ajouter des informations aux centres mais leur création
            se fait via l'import.
          </span>
        </div>
        <div v-if="!filteredCenters || filteredCenters.length === 0">
          Aucun centre trouvé.
        </div>
        <template v-for="center of currentCenters" :key="center.id">
          <HealthCenterDisplay
            :class="{
              hide: currentCenter && currentCenter.id !== center.id,
              selected: currentCenter && currentCenter.id === center.id,
            }"
            :modelValue="center"
            openMode="Edit"
            :minified="!currentCenter || currentCenter.id !== center.id"
            @click:detail="centerSelected"
          />
        </template>
        <div v-if="currentPage < maxPage" class="center">
          <span class="button" @click="loadMore">Montrer plus</span>
        </div>
      </div>
    </div>
    <Navigation nav-key="Dossiers associés au centre">
      <RelatedDossier
        :entity="currentCenter"
        :allow-creation="false"
        :panel-x="panelX"
        v-if="currentCenter !== null"
      />
    </Navigation>
  </div>
</template>

<style scoped lang="scss">
.info {
  font-size: small;
}

.center {
  align-items: center;
  display: flex;
  flex-direction: row;
  flex-wrap: nowrap;
  justify-content: space-evenly;
}

.centers {
  display: flex;
  flex-direction: row;
  column-gap: 3em;

  .container-cntrs {
    display: flex;
    flex-direction: column;
    padding-top: 1em;
    margin-left: 1em;
    margin-right: 1em;

    .container-stats {
      display: flex;
      flex-direction: row;
      align-items: baseline;
      justify-content: space-between;
    }

    .container-count {
      text-align: right;
    }
  }

  .container-listing {
    display: flex;
    flex-direction: column;
    padding-top: 1em;
    row-gap: 4em;
    margin-bottom: 100px;
  }

  .group {
    display: flex;
    flex-direction: row;

    &.split {
      justify-content: space-between;
    }
  }

  .hide {
    opacity: 0.2;
  }
}
</style>
